import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { ConversationMessage } from '@/types/residents/marketplaceListing';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { showConfirmWithButtons } from '@/utils/common/alert';
import AttachmentPreview from '@/components/residents/propertyChat/mobile/AttachmentPreview';
import {
  DateSeparator,
  SentMessageBubble,
  ReceivedMessageBubble,
  ChatInputBar,
  UserAvatar,
  groupMessagesByDate,
  useChatStyles,
  useChatColors,
} from './shared';
import {
  useGetConversationByListingQuery,
  useSendMessageMutation,
} from '@/store/api';

type NavigationProp = StackNavigationProp<RootStackParamList>;

interface InlineConversationProps {
  listingId: string;
  onConversationStart?: (conversationId: string) => void;
  /** Called when the input is focused, allowing parent to scroll */
  onInputFocus?: () => void;
}

/**
 * Inline conversation component for displaying chat within the item detail screen.
 * Used by buyers to see their conversation with the seller.
 */
export const InlineConversation: React.FC<InlineConversationProps> = ({
  listingId,
  onConversationStart,
  onInputFocus,
}) => {
  const navigation = useNavigation<NavigationProp>();
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const chatStyles = useChatStyles();
  const chatColors = useChatColors();
  const styles = useMemo(() => buildStyles(MARKETPLACE_COLORS), [MARKETPLACE_COLORS]);
  const scrollViewRef = useRef<ScrollView>(null);
  const textInputRef = useRef<TextInput>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Always fetch conversation data with polling enabled
  // This ensures refetch() works after sending messages and polling stays active
  const { 
    data: conversationData, 
    isLoading,
    refetch,
  } = useGetConversationByListingQuery(listingId, {
    pollingInterval: 10000, // Poll every 10 seconds for new messages
  });

  // Send message mutation
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  // Local state
  const [messageText, setMessageText] = useState('');

  // Attachment handling
  const { 
    attachments,
    filesForApi,
    addFromCamera, 
    addFromLibrary, 
    addFromWebFilePicker,
    removeAttachment, 
    resetAttachments,
  } = useChatAttachments();

  // Auto scroll to bottom when messages change
  useEffect(() => {
    if (conversationData?.messages && conversationData.messages.length > 0) {
      // Clear any existing timeout to prevent memory leaks
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
    
    // Cleanup timeout on unmount or dependency change
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [conversationData?.messages]);

  const handleSend = async () => {
    const trimmedMessage = messageText.trim();
    const hasFiles = attachments.length > 0;
    
    if ((!trimmedMessage && !hasFiles) || isSending) return;
    if (!conversationData) return;

    // Get conversation ID from the data
    const conversationId = conversationData.id;
    if (!conversationId) return;

    setMessageText('');
    const filesToSend = filesForApi;
    resetAttachments();

    try {
      await sendMessage({
        conversationId,
        text: trimmedMessage || undefined,
        files: filesToSend,
        listingId, // Include listingId for cache invalidation
      }).unwrap();
      
      // Refetch to get the new message
      refetch();
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessageText(trimmedMessage);
    }
  };

  const handleAttach = () => {
    // On web, use the web file picker with image-only MIME type validation
    if (Platform.OS === 'web') {
      addFromWebFilePicker();
      return;
    }
    // On native, show camera/library options
    showConfirmWithButtons(
      'Add Photos',
      'Choose how you want to add photos',
      'Photo Library',
      'Take Photo',
      addFromLibrary,
      addFromCamera
    );
  };

  const handleViewFullConversation = () => {
    if (conversationData) {
      navigation.navigate('InboxConversation', { conversationId: conversationData.id });
    }
  };

  // Group messages by date for display
  const messageGroups = conversationData?.messages 
    ? groupMessagesByDate(conversationData.messages)
    : [];

  // Render messages
  const renderMessages = () => {
    if (!conversationData?.messages || conversationData.messages.length === 0) {
      return null;
    }

    return messageGroups.map((group, groupIndex) => (
      <View key={`group-${groupIndex}`} style={chatStyles.messageGroup}>
        <DateSeparator date={group.date} />
        <View style={chatStyles.messagesInGroup}>
          {group.messages.map((message: ConversationMessage) => {
            if (message.sender === 'user') {
              return (
                <SentMessageBubble
                  key={message.id}
                  text={message.text}
                  timestamp={message.timestamp}
                  files={message.files}
                />
              );
            } else {
              return (
                <ReceivedMessageBubble
                  key={message.id}
                  text={message.text}
                  timestamp={message.timestamp}
                  userName={conversationData.otherUser.name}
                  userInitials={conversationData.otherUser.initials}
                  files={message.files}
                />
              );
            }
          })}
        </View>
      </View>
    ));
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={MARKETPLACE_COLORS.accentRed} />
        </View>
      </View>
    );
  }

  if (!conversationData) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <UserAvatar 
          initials={conversationData.otherUser.initials}
          avatarUrl={conversationData.otherUser.avatarUrl}
          size="small"
        />
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>Chat with {conversationData.otherUser.name}</Text>
          <Text style={styles.headerSubtitle}>
            {conversationData.messages.length} message{conversationData.messages.length !== 1 ? 's' : ''}
          </Text>
        </View>
      </View>

      {/* Messages */}
      <ScrollView
        ref={scrollViewRef}
        style={styles.messagesContainer}
        contentContainerStyle={styles.messagesContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={chatStyles.messagesContainer}>
          {renderMessages()}
        </View>
      </ScrollView>

      {/* Attachment Preview */}
      {attachments.length > 0 && (
        <AttachmentPreview 
          attachments={attachments} 
          onRemove={removeAttachment} 
        />
      )}

      {/* Input Bar */}
      <ChatInputBar
        value={messageText}
        onChangeText={setMessageText}
        onSend={handleSend}
        onAttach={handleAttach}
        inputRef={textInputRef}
        paddingBottom={16}
        hasAttachments={attachments.length > 0}
        onFocus={onInputFocus}
      />
    </View>
  );
};

type MarketplaceColors = ReturnType<typeof useMarketplaceColors>;

const buildStyles = (MARKETPLACE_COLORS: MarketplaceColors) => StyleSheet.create({
  container: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.dividerGray,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  headerTextContainer: {
    flex: 1,
    gap: 2,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  headerSubtitle: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  messagesContainer: {
    maxHeight: 300,
  },
  messagesContent: {
    paddingVertical: 16,
  },
});

